// Established clinical early-warning scores, implemented exactly from the published charts.
// Decision support only: the score never diagnoses, and the clinician's judgement and the clinic's
// own escalation pathway always apply. The charts are fixed (not editable); a change to any table
// is a code change with tests (scores.test.ts) and a clinical safety review.
//
// - MEWS: NHS England national Maternity Early Warning Score (pregnant women; NEWS2 excludes pregnancy).
// - NEWS2: Royal College of Physicians, 2017 (adults, not pregnant). SpO2 Scale 1 only.
// - Young-infant danger signs: WHO IMNCI "possible serious bacterial infection" signs (0–59 days),
//   and IMCI general danger signs + fast breathing for older babies.

export type ScoreBand = 'low' | 'lowMedium' | 'medium' | 'high';

export interface ScorePart { key: string; value: number | string; points: number }

export interface ScoreResult {
  scoreId: 'mews' | 'news2';
  total: number;
  band: ScoreBand;
  parts: ScorePart[];
  missing: string[]; // parameters not recorded: the total may be falsely low
  redFlag?: string; // e.g. a single NEWS2 parameter scoring 3
}

/** Upper bounds (inclusive) with points; the last entry has no bound. */
type Bands = [number, number][];
const pts = (v: number, bands: Bands, last: number) => {
  for (const [upTo, p] of bands) if (v <= upTo) return p;
  return last;
};
const num = (raw: unknown): number | undefined => {
  if (raw === undefined || raw === null || raw === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

// ---------------------------------------------------------------- MEWS (national, NHS England)

export interface MewsInput { respRate?: unknown; spo2?: unknown; temp?: unknown; pulse?: unknown; bpSys?: unknown; bpDia?: unknown }

const MEWS: Record<keyof MewsInput, { bands: Bands; last: number }> = {
  respRate: { bands: [[6, 2], [8, 1], [21, 0], [24, 1]], last: 2 },
  spo2: { bands: [[92, 2], [94, 1]], last: 0 },
  temp: { bands: [[35.6, 2], [36.1, 1], [37.2, 0], [37.4, 1]], last: 2 },
  pulse: { bands: [[62, 2], [70, 1], [112, 0], [121, 1]], last: 2 },
  bpSys: { bands: [[93, 2], [100, 1], [135, 0], [144, 1]], last: 2 },
  bpDia: { bands: [[56, 2], [61, 1], [88, 0], [96, 1]], last: 2 },
};
// From 48 hours after birth the chart uses a lower pulse band.
const MEWS_PULSE_POSTNATAL = { bands: [[50, 2], [57, 1], [98, 0], [107, 1]] as Bands, last: 2 };

export const mewsBand = (total: number): ScoreBand => (total >= 8 ? 'high' : total >= 5 ? 'medium' : total >= 2 ? 'lowMedium' : 'low');

export function mews(input: MewsInput, opts: { postnatal48h?: boolean } = {}): ScoreResult {
  const parts: ScorePart[] = [];
  const missing: string[] = [];
  (Object.keys(MEWS) as (keyof MewsInput)[]).forEach((k) => {
    const v = num(input[k]);
    if (v === undefined) return missing.push(k);
    const t = k === 'pulse' && opts.postnatal48h ? MEWS_PULSE_POSTNATAL : MEWS[k];
    parts.push({ key: k, value: v, points: pts(v, t.bands, t.last) });
  });
  const total = parts.reduce((s, p) => s + p.points, 0);
  return { scoreId: 'mews', total, band: mewsBand(total), parts, missing };
}

// ---------------------------------------------------------------- NEWS2 (RCP 2017)

export interface News2Input { respRate?: unknown; spo2?: unknown; onOxygen?: boolean; bpSys?: unknown; pulse?: unknown; alert?: boolean; temp?: unknown }

const NEWS2: Record<'respRate' | 'spo2' | 'bpSys' | 'pulse' | 'temp', { bands: Bands; last: number }> = {
  respRate: { bands: [[8, 3], [11, 1], [20, 0], [24, 2]], last: 3 },
  spo2: { bands: [[91, 3], [93, 2], [95, 1]], last: 0 }, // Scale 1
  bpSys: { bands: [[90, 3], [100, 2], [110, 1], [219, 0]], last: 3 },
  pulse: { bands: [[40, 3], [50, 1], [90, 0], [110, 1], [130, 2]], last: 3 },
  temp: { bands: [[35.0, 3], [36.0, 1], [38.0, 0], [39.0, 1]], last: 2 },
};

export function news2(input: News2Input): ScoreResult {
  const parts: ScorePart[] = [];
  const missing: string[] = [];
  (Object.keys(NEWS2) as (keyof typeof NEWS2)[]).forEach((k) => {
    const v = num(input[k]);
    if (v === undefined) return missing.push(k);
    parts.push({ key: k, value: v, points: pts(v, NEWS2[k].bands, NEWS2[k].last) });
  });
  if (input.onOxygen === undefined) missing.push('onOxygen');
  else parts.push({ key: 'onOxygen', value: input.onOxygen ? 'oxygen' : 'air', points: input.onOxygen ? 2 : 0 });
  if (input.alert === undefined) missing.push('alert');
  else parts.push({ key: 'alert', value: input.alert ? 'A' : 'CVPU', points: input.alert ? 0 : 3 });
  const total = parts.reduce((s, p) => s + p.points, 0);
  const single3 = parts.find((p) => p.points === 3);
  const band: ScoreBand = total >= 7 ? 'high' : total >= 5 ? 'medium' : single3 ? 'lowMedium' : 'low';
  return { scoreId: 'news2', total, band, parts, missing, ...(single3 ? { redFlag: single3.key } : {}) };
}

// ---------------------------------------------------------------- Baby danger signs (WHO)

export interface BabySignsInput {
  ageDays?: number; // unknown = treated as a young infant (the stricter rules)
  temp?: unknown; // °C
  respRate?: unknown; // breaths per minute
  notFeeding?: boolean;
  convulsions?: boolean;
  reducedMovement?: boolean; // moves only when stimulated, or not at all
  chestIndrawing?: boolean;
}

export interface BabySignsResult { signs: string[]; urgent: boolean; youngInfant: boolean; missing: string[] }

/** Any one sign = urgent: same-day assessment / referral by the clinic. */
export function babyDangerSigns(i: BabySignsInput): BabySignsResult {
  const young = i.ageDays === undefined || i.ageDays < 60;
  const signs: string[] = [];
  const missing: string[] = [];
  if (i.notFeeding) signs.push('notFeeding');
  if (i.convulsions) signs.push('convulsions');
  if (i.reducedMovement) signs.push('reducedMovement');
  if (i.chestIndrawing) signs.push('chestIndrawing');
  const t = num(i.temp);
  if (t === undefined) missing.push('temp');
  else if (young && t >= 38) signs.push('fever');
  else if (young && t < 35.5) signs.push('lowTemp');
  const rr = num(i.respRate);
  // Fast breathing: 60+/min under 2 months; 50+ at 2–12 months; 40+ from 12 months (WHO IMCI).
  const fast = i.ageDays === undefined || i.ageDays < 60 ? 60 : i.ageDays < 365 ? 50 : 40;
  if (rr === undefined) missing.push('respRate');
  else if (rr >= fast) signs.push('fastBreathing');
  return { signs, urgent: signs.length > 0, youngInfant: young, missing };
}

export const SCORE_SOURCES = {
  mews: { name: 'National MEWS (NHS England)', url: 'https://portal.e-lfh.org.uk/ContentServer/content/MEW_01_001/course/en/assets/National-MEWS-Final-version.pdf' },
  news2: { name: 'NEWS2 (Royal College of Physicians, 2017)', url: 'https://www.rcp.ac.uk/improving-care/resources/national-early-warning-score-news-2/' },
  baby: { name: 'WHO IMNCI young-infant danger signs', url: 'https://www.ncbi.nlm.nih.gov/books/NBK610598/' },
};
